import React, { useCallback, useEffect, useState } from 'react';
import { PendingEntry, ChangeProposal, PendingEntryStatus } from '../../types/workflow';
import { workflowService } from '../../services/workflowService';
import { asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { Page, PageHeader, SectionHeader, StatGrid, Tabs } from '../ui';
import { PendingEntriesList } from './PendingEntriesList';
import { ChangeProposalsList } from './ChangeProposalsList';
import { ReviewInterface } from './ReviewInterface';

const TAB_VALUES = ['pending-entries', 'change-proposals', 'my-assignments', 'my-submissions'] as const;
type TabType = (typeof TAB_VALUES)[number];

/* One codec per parameter, made once: the URL state reads them on every render */
const TAB = asOneOf(TAB_VALUES);

interface Counts {
  entries: number;
  proposals: number;
  assigned: number;
}

export const WorkflowDashboard: React.FC = () => {
  // The queue on screen and what is being reviewed are in the address bar
  const [activeTab, setActiveTab] = useUrlState<TabType>('tab', 'pending-entries', TAB);
  const [entryId, setEntryId] = useUrlState('entry', '');
  const [proposalId, setProposalId] = useUrlState('proposal', '');
  const write = useUrlWriter();

  // A row hands over the whole record; the URL carries only its id
  const [knownEntry, setKnownEntry] = useState<PendingEntry | null>(null);
  const [knownProposal, setKnownProposal] = useState<ChangeProposal | null>(null);
  const selectedEntry = knownEntry && knownEntry.id === entryId ? knownEntry : null;
  const selectedProposal = knownProposal && knownProposal.id === proposalId ? knownProposal : null;

  const [refreshKey, setRefreshKey] = useState(0);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [countsVersion, setCountsVersion] = useState(0);

  // Pending totals for the header; one row per request, only the totals are used
  const loadCounts = useCallback(async () => {
    const query = { status: PendingEntryStatus.PENDING, page: 1, limit: 1 };
    try {
      const [entries, proposals, mine] = await Promise.all([
        workflowService.getPendingEntries(query),
        workflowService.getChangeProposals(query),
        workflowService.getMyAssignments(query),
      ]);
      setCounts({
        entries: entries?.total ?? 0,
        proposals: proposals?.total ?? 0,
        assigned: (mine?.pendingEntries?.total ?? 0) + (mine?.changeProposals?.total ?? 0),
      });
    } catch {
      setCounts(null);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts, refreshKey, countsVersion]);

  /*
   * Nothing can be reviewed that a list has not handed over — there is no
   * endpoint for one entry — so an id the queues never produced opens the
   * queue rather than an empty dialog.
   */
  useEffect(() => {
    if ((entryId && !selectedEntry) || (proposalId && !selectedProposal)) {
      write({ entry: selectedEntry ? entryId : null, proposal: selectedProposal ? proposalId : null });
    }
  }, [entryId, proposalId, selectedEntry, selectedProposal, write]);

  const closeReview = useCallback(() => write({ entry: null, proposal: null }), [write]);

  const handleReviewComplete = () => {
    closeReview();
    setRefreshKey(prev => prev + 1); // Force refresh of lists
  };

  const selectEntry = (entry: PendingEntry) => {
    setKnownEntry(entry);
    setEntryId(entry.id);
  };

  const selectProposal = (proposal: ChangeProposal) => {
    setKnownProposal(proposal);
    setProposalId(proposal.id);
  };

  const handleReviewed = () => setCountsVersion(v => v + 1);

  const tabs: Array<{ value: TabType; label: string }> = [
    { value: 'pending-entries', label: 'Entries' },
    { value: 'change-proposals', label: 'Changes' },
    { value: 'my-assignments', label: 'Assigned' },
    { value: 'my-submissions', label: 'Mine' },
  ];

  const format = (n?: number) => (counts ? (n ?? 0).toLocaleString() : '—');
  const totalPending = counts ? counts.entries + counts.proposals : null;

  return (
    <Page>
      <PageHeader
        title="Pending"
        meta={totalPending === null ? ' ' : totalPending === 0 ? 'All clear' : `${totalPending} to review`}
      />

      {/* Three columns on phones too; the kit grid pairs cells, so re-draw the third cell's hairline */}
      <StatGrid
        className="mb-6 grid-cols-3 [&>*:nth-child(3)]:border-l [&>*:nth-child(3)]:border-t-0"
        items={[
          { label: 'New entries', value: format(counts?.entries) },
          { label: 'Changes', value: format(counts?.proposals) },
          { label: 'Assigned', value: format(counts?.assigned) },
        ]}
      />

      <Tabs<TabType> label="Queue" className="mb-4" items={tabs} value={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'pending-entries' && (
        <PendingEntriesList
          key={`pending-${refreshKey}`}
          scope="entries"
          onSelectEntry={selectEntry}
          onReviewed={handleReviewed}
          showFilters={true}
        />
      )}

      {activeTab === 'change-proposals' && (
        <ChangeProposalsList
          key={`proposals-${refreshKey}`}
          scope="changes"
          onSelectProposal={selectProposal}
          onReviewed={handleReviewed}
          showFilters={true}
        />
      )}

      {activeTab === 'my-assignments' && (
        <div className="space-y-8">
          <section>
            <SectionHeader title="Entries" />
            <PendingEntriesList
              key={`my-pending-${refreshKey}`}
              scope="assignedEntries"
              onSelectEntry={selectEntry}
              onReviewed={handleReviewed}
              showFilters={true}
              assignedToMe={true}
            />
          </section>

          <section>
            <SectionHeader title="Changes" />
            <ChangeProposalsList
              key={`my-proposals-${refreshKey}`}
              scope="assignedChanges"
              onSelectProposal={selectProposal}
              onReviewed={handleReviewed}
              showFilters={true}
              assignedToMe={true}
            />
          </section>
        </div>
      )}

      {activeTab === 'my-submissions' && (
        <div className="space-y-8">
          <section>
            <SectionHeader title="Entries" />
            <PendingEntriesList
              key={`submitted-pending-${refreshKey}`}
              scope="submittedEntries"
              onSelectEntry={selectEntry}
              onReviewed={handleReviewed}
              showFilters={true}
            />
          </section>

          <section>
            <SectionHeader title="Changes" />
            <ChangeProposalsList
              key={`submitted-proposals-${refreshKey}`}
              scope="submittedChanges"
              onSelectProposal={selectProposal}
              onReviewed={handleReviewed}
              showFilters={true}
            />
          </section>
        </div>
      )}

      {/* Review dialog */}
      {(selectedEntry || selectedProposal) && (
        <ReviewInterface
          entry={selectedEntry || undefined}
          proposal={selectedProposal || undefined}
          onReviewComplete={handleReviewComplete}
          onClose={closeReview}
        />
      )}
    </Page>
  );
};
